import { Warning } from '@mui/icons-material'
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
} from '@mui/material'
import { useMemo, useRef, useState } from 'react'
import { useUser } from '@/features/auth/hooks/useUser'
import config from '@/features/bulk-edit/config'
import { useSearch } from '@/features/search/hooks/useSearch'
import { useAppSelector } from '@/redux/hooks'

export const BulkEditSelected = () => {
  const [showPopover, setShowPopover] = useState(false)
  const ref = useRef(null)
  const { selectors: searchSelectors } = useSearch()
  const {
    selectors: { selectUserData, selectUserRights, selectUserDelegates },
  } = useUser()

  const userData = selectUserData()
  const userRights = selectUserRights()
  const userDelegates = selectUserDelegates()

  const selection = useAppSelector(searchSelectors.selectCurrentSelectionRows)
  const first = selection[0]

  const disabled = selection.length === 0

  const bulkEditUrl = useAppSelector(searchSelectors.selectBulkEditQueryString)

  const warnings = useMemo(() => {
    /* IMPROVEMENT:
     Would be nice to handle some of these warnings for the user,
     like removing process owned items or selecting a media type.
    */
    const _warnings = []

    const allItemsAreSameType = selection.every(
      (item) => item.mediaType === first.mediaType,
    )
    const userHasRights =
      userRights.canEditAll ||
      selection.every(
        (i) =>
          userData?.catloginid &&
          [userData.catloginid, ...userDelegates].includes(i.ownerCwsId),
      )

    const itemsAreOwnedByProcess = selection.some((i) =>
      config.processOwners.includes(i.ownerCwsId),
    )

    if (selection.length > config.maxBulkEditItems)
      _warnings.push(
        <BulkEditWarning key="limit" message="Limit of 50 items" />,
      )

    if (!allItemsAreSameType)
      _warnings.push(
        <BulkEditWarning
          key="mixed"
          message="Bulk Edit cannot be performed on mixed types"
        />,
      )

    if (!userHasRights)
      _warnings.push(
        <BulkEditWarning
          key="not-owner"
          message="Not owner or delegate of all items"
        />,
      )

    if (itemsAreOwnedByProcess)
      _warnings.push(
        <BulkEditWarning
          key="owned-by-process"
          message="Some items are owned by a process and cannot be edited"
        />,
      )

    return _warnings
  }, [
    selection,
    userRights,
    first?.mediaType,
    userData?.catloginid,
    userDelegates,
  ])
  const hasWarnings = warnings.length > 0

  const handleClick = () => {
    if (hasWarnings) return

    if (bulkEditUrl) {
      open(bulkEditUrl, '_new')
    }
  }

  const handleMouseEnter = () => {
    setShowPopover(true)
  }
  const handleMouseLeave = () => {
    setShowPopover(false)
  }

  return (
    <>
      <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <Button
          ref={ref}
          color={hasWarnings ? 'warning' : undefined}
          startIcon={hasWarnings ? <Warning /> : null}
          onClick={handleClick}
          disabled={disabled}
        >
          Bulk Edit
        </Button>
      </Box>
      <Popover
        open={hasWarnings && showPopover}
        anchorEl={ref.current}
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        disableRestoreFocus
      >
        <List>{warnings}</List>
      </Popover>
    </>
  )
}

type WarningProps = {
  message: string
}
const BulkEditWarning = (props: WarningProps) => (
  <ListItem>
    <ListItemIcon>
      <Warning fontSize="large" color="warning" />
    </ListItemIcon>
    <ListItemText>{props.message}</ListItemText>
  </ListItem>
)
